import CustomTooltip from '../../../shared/CustomTooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    IconButton,
    useTheme,
    Stack,
    Typography,
} from '@mui/material';
import { keyframes } from '@mui/system';
import React, { useState } from 'react';
import {
    camelCaseToLowerCase,
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
} from '../../../../shared/utils';
import { Period, Space } from '../../../types/experience';
import SpaceModal from './SpaceModal';
import { SpaceIcons } from '../SpaceCard';
import { useFormContext } from 'react-hook-form';
import { SpaceType } from '../../../enums/experience';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { format, startOfDay } from 'date-fns';

export const getBeddingArray = (space: Space): [string, number][] => {
    return Object.entries(space).filter(
        ([key, value]) =>
            [
                'singleBed',
                'doubleBed',
                'queenBed',
                'kingBed',
                'bunkBed',
                'sofaBed',
                'couch',
                'babyCot',
            ].includes(key) && Number(value) > 0,
    );
};

export const getAmenitiesArray = (space: Space): string[] => {
    return Object.entries(space)
        .filter(
            ([key, value]) =>
                value === true &&
                !(key === 'newSpace' || key === 'isAvailable'),
        )
        .map(([key]) => key);
};

const fadeInZoomAnimation = keyframes`
    from {
        opacity: 0; 
        transform: scale(0.95); 
    }
    to {
        opacity: 1; 
        transform: scale(1); 
    }
`;

export type ModifiedSpace = Space & {
    newSpace?: boolean;
    name: string;
    /** space id */
    id?: number;
    /** RHF field id */
    // _id: string;
    isAvailable?: boolean;
    assignedName?: string;
};

const IndividualSpace = ({
    space,
    handleDelete,
    handleDuplicate,
    handleUpdate,
    index,
    type,
    subType,
}: {
    space: ModifiedSpace;
    handleDelete: (i: number) => void;
    handleDuplicate: (space: ModifiedSpace) => void;
    handleUpdate: (updatedSpace: Space) => void;
    index: number;
    type?: string;
    subType?: string;
}) => {
    const {
        formState: { errors },
    } = useFormContext();

    const [showModal, setShowModal] = useState(false);
    const theme = useTheme();

    const spaceError = (errors as any)?.spaces?.[index];
    const hasSpaceError = !!spaceError;
    const hasSleepingCapacityError = !!spaceError?.sleepingCapacity;
    const isUnavailable = !space.isAvailable;
    const shouldErrorHighlight = hasSpaceError || isUnavailable;

    const handleEditSpace = (updatedSpace: Space) => {
        handleUpdate({ ...space, ...updatedSpace });
        setShowModal(false);
    };

    const isBedroom = space.spaceType === SpaceType.Bedroom;
    const title =
        space?.assignedName && space?.assignedName?.length > 0
            ? space?.assignedName
            : camelCaseToTitleCase(space.name);

    const availabilityLabel = !space.isAvailable
        ? 'Unavailable'
        : space.unavailablePeriods.filter(
                (p: Period) =>
                    new Date(p.to) >= startOfDay(new Date()) &&
                    p.reason !== 'reserved',
            ).length > 0
          ? 'Partially Available'
          : '';

    const upcomingNonReservedPeriods = (space.unavailablePeriods ?? [])
        .filter(
            (p: Period) =>
                new Date(p.to) >= startOfDay(new Date()) &&
                p.reason !== 'reserved',
        )
        .sort((a: Period, b: Period) => a.from.localeCompare(b.from));

    const nextBlockedPeriod = upcomingNonReservedPeriods[0] as
        | Period
        | undefined;

    const formatChipDate = (value: string) => {
        const parsed = new Date(`${value}T00:00:00`);
        return Number.isNaN(parsed.getTime())
            ? value
            : format(parsed, 'dd/MM/yyyy');
    };

    const sectionValueSx = {
        fontSize: '13px',
        color: 'text.secondary',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: 2,
        overflow: 'hidden',
    };

    return (
        <Grid item xs={1}>
            <Card
                onClick={() => {
                    setShowModal(true);
                }}
                sx={{
                    // Animation helps visually indicate where the new space is added, as spaces are sorted alphabetically.
                    animation: space.newSpace
                        ? `${fadeInZoomAnimation} 0.3s ease-out`
                        : 'none',
                    transformOrigin: 'center',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    cursor: 'pointer',
                    height: '100%',
                    minHeight: '250px',
                    transition:
                        'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                        boxShadow: !space.isAvailable ? 0 : 3,
                        borderColor: !space.isAvailable
                            ? theme.palette.custom.lightBorderColor
                            : theme.palette.primary.light,
                        transform: !space.isAvailable
                            ? 'none'
                            : 'translateY(-1px)',
                    },
                }}
                style={{ flex: 1 }}
            >
                <Box
                    sx={{
                        px: 1.5,
                        pt: 1.25,
                        pb: 1,
                        backgroundColor:
                            theme.palette.custom.cardActionBackground,
                    }}
                >
                    <Stack
                        direction='row'
                        alignItems='flex-start'
                        justifyContent='space-between'
                        gap={1}
                    >
                        <Stack direction='row' alignItems='center' gap={1}>
                            <Box
                                sx={{
                                    color: shouldErrorHighlight
                                        ? 'error.main'
                                        : 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {SpaceIcons[space.spaceType] || null}
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    color: shouldErrorHighlight
                                        ? 'error.main'
                                        : 'text.primary',
                                    lineHeight: 1.2,
                                }}
                            >
                                {title}
                            </Typography>
                        </Stack>

                        <Stack
                            direction='row'
                            alignItems='center'
                            spacing={0.25}
                        >
                            <CustomTooltip title='Duplicate'>
                                <IconButton
                                    size='small'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicate(space);
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </CustomTooltip>
                            <CustomTooltip title='Delete'>
                                <IconButton
                                    size='small'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(index);
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </CustomTooltip>
                            <CustomTooltip title='Edit'>
                                <IconButton
                                    size='small'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowModal(true);
                                    }}
                                >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </CustomTooltip>
                        </Stack>
                    </Stack>

                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        flexWrap='wrap'
                        sx={{
                            pt: 0.75,
                            minHeight: 28, // keeps header heights consistent whether pill exists or not
                        }}
                    >
                        {availabilityLabel ? (
                            <CustomTooltip
                                title={
                                    !space.isAvailable
                                        ? 'This space is not bookable.'
                                        : nextBlockedPeriod
                                          ? `Some dates are blocked: ${formatChipDate(nextBlockedPeriod.from)} → ${formatChipDate(nextBlockedPeriod.to)}`
                                          : 'Some dates are blocked.'
                                }
                            >
                                <Box component='span'>
                                    <CustomChip
                                        color={
                                            !space.isAvailable
                                                ? 'error'
                                                : 'default'
                                        }
                                        size='small'
                                        variant='outlined'
                                        label={availabilityLabel}
                                        sx={{
                                            minWidth: 'unset',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            ...(space.isAvailable
                                                ? {
                                                      borderColor:
                                                          theme.palette.yellow
                                                              .main,
                                                      color: theme.palette
                                                          .yellow.main,
                                                  }
                                                : {}),
                                        }}
                                    />
                                </Box>
                            </CustomTooltip>
                        ) : null}
                    </Stack>
                </Box>

                <Divider />
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        height: 'auto',
                        overflow: 'visible',
                        px: 1.75,
                        py: 1.5,
                    }}
                >
                    {getBeddingArray(space).length > 0 && (
                        <Stack spacing={0.5}>
                            <Typography
                                variant='caption'
                                sx={{ fontWeight: 800 }}
                            >
                                Sleeping arrangements:
                            </Typography>
                            <Typography variant='body3' sx={sectionValueSx}>
                                {getBeddingArray(space)
                                    .map(
                                        (item: any) =>
                                            `${item[1]} ${camelCaseToLowerCase(item[0])}${
                                                item[1] > 1 &&
                                                item[0] !== 'couch'
                                                    ? 's'
                                                    : item[1] > 1 &&
                                                        item[0] === 'couch'
                                                      ? 'es'
                                                      : ''
                                            }`,
                                    )
                                    .join(', ')}
                            </Typography>
                        </Stack>
                    )}
                    {isBedroom && 'sleepingCapacity' in space && (
                        <Stack spacing={0.5}>
                            <Typography
                                variant='caption'
                                sx={{
                                    fontWeight: 800,
                                    color: hasSleepingCapacityError
                                        ? 'error.main'
                                        : 'text.primary',
                                }}
                            >
                                Sleeping Capacity:
                            </Typography>
                            <Typography
                                variant='body3'
                                sx={{
                                    ...sectionValueSx,
                                    color:
                                        space.sleepingCapacity &&
                                        space.sleepingCapacity > 0
                                            ? 'text.secondary'
                                            : 'error.main',
                                }}
                            >
                                {space.sleepingCapacity &&
                                space.sleepingCapacity > 0 ? (
                                    <span>
                                        {space.sleepingCapacity} guest
                                        {space.sleepingCapacity > 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span>Not set</span>
                                )}
                            </Typography>
                        </Stack>
                    )}
                    {getAmenitiesArray(space).length > 0 && (
                        <Stack spacing={0.5}>
                            <Typography
                                variant='caption'
                                sx={{ fontWeight: 800 }}
                            >
                                Amenities:
                            </Typography>
                            <Typography variant='body3' sx={sectionValueSx}>
                                {getAmenitiesArray(space)
                                    .map((amenity: any) =>
                                        camelCaseToSentenceCase(amenity),
                                    )
                                    .join(', ')}
                            </Typography>
                        </Stack>
                    )}
                    {'desk' in space && space.desk > 0 && (
                        <Stack spacing={0.5}>
                            <Typography
                                variant='caption'
                                sx={{ fontWeight: 800 }}
                            >
                                Workspace:
                            </Typography>
                            <Typography variant='body3' sx={sectionValueSx}>
                                <span>
                                    {space.desk} desk{space.desk > 1 ? 's' : ''}
                                </span>
                            </Typography>
                        </Stack>
                    )}

                    {'seatingCapacity' in space &&
                        space.seatingCapacity > 0 && (
                            <Stack spacing={0.5}>
                                <Typography
                                    variant='caption'
                                    sx={{ fontWeight: 800 }}
                                >
                                    Seating Capacity:
                                </Typography>
                                <Typography variant='body3' sx={sectionValueSx}>
                                    <span>{space.seatingCapacity} people</span>
                                </Typography>{' '}
                            </Stack>
                        )}
                    {'parkingCapacity' in space &&
                        space.parkingCapacity > 0 && (
                            <Stack spacing={0.5}>
                                <Typography
                                    variant='caption'
                                    sx={{ fontWeight: 800 }}
                                >
                                    Parking Capacity:
                                </Typography>
                                <Typography variant='body3' sx={sectionValueSx}>
                                    <span>{space.parkingCapacity} cars</span>
                                </Typography>
                            </Stack>
                        )}
                </CardContent>
            </Card>
            <SpaceModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={handleEditSpace}
                existingSpace={space}
                type={type}
                subType={subType}
            />
        </Grid>
    );
};

export default IndividualSpace;
